import React, { useState, useEffect, useCallback } from "react";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import config from "../config";
import "../styles/Login.css";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  const checkProfileAndNavigate = useCallback(async () => {
    console.log("Checking profile for navigation...");
    const user = auth.currentUser;
    if (!user) {
      console.log("No user found, cannot check profile.");
      return false;
    }

    try {
      const token = await user.getIdToken();
      if (!token) throw new Error("Failed to get auth token.");

      const response = await fetch(`${config.apiUrl}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok)
        throw new Error(`Profile fetch failed: ${response.statusText}`);
      const profile = await response.json();
      console.log("Profile received:", profile);

      if (profile.playerGame?.gameId) {
        console.log(
          `Navigating player to game: /game/${profile.playerGame.gameId}`
        );
        navigate(`/game/${profile.playerGame.gameId}`);
        return true;
      }

      if (profile.activePagePath) {
        console.log(
          `Navigating owner to home (for page: ${profile.activePagePath})`
        );
        navigate("/home");
        return true;
      }

      console.log(
        "User is logged in but not active player or owner. No automatic navigation."
      );
      return false;
    } catch (error) {
      console.error("Error checking profile or navigating:", error);
      return false;
    }
  }, [navigate]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      console.log("Login Page Auth Check, User:", user?.uid);
      setCurrentUser(user);
      setAuthChecked(true);

      if (user) {
        setLoading(true);
        const navigated = await checkProfileAndNavigate();
        if (!navigated) {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [checkProfileAndNavigate]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      await checkProfileAndNavigate();
    } catch (error) {
      console.error("Authentication error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      await checkProfileAndNavigate();
    } catch (error) {
      console.error("Authentication error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = async () => {
    console.log("handleLogout called");
    setActionLoading(true);
    try {
      await signOut(auth);
      console.log("User signed out successfully via signOut().");
    } catch (error) {
      console.error("Sign out error:", error);
      alert(`Sign out failed: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const goToCreateGame = () => {
    console.log("goToCreateGame called, navigating to /home");
    navigate("/home");
  };

  const goToJoinGame = () => {
    navigate("/join");
  };

  if (loading || !authChecked) {
    return (
      <div className="login-container">
        <div className="login-form">
          <h3>Loading...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-shape login-shape-primary"></div>
        <div className="login-shape login-shape-secondary"></div>
      </div>

      {currentUser ? (
        // --- Logged-in View ---
        <div className="login-form">
          <h3>Welcome Back!</h3>
          <p style={{ textAlign: "center" }}>
            Logged in as: {currentUser.email}
          </p>
          <button
            className="login-button"
            onClick={goToCreateGame}
            disabled={actionLoading}
          >
            {actionLoading ? "Loading..." : "Create or Revisit your Game"}
          </button>
          <button
            className="signup-button"
            onClick={handleLogout}
            disabled={actionLoading}
          >
            {actionLoading ? "Logging Out..." : "Logout"}
          </button>
          <div className="join-game-container">
            <button
              type="button"
              className="join-game-button"
              onClick={goToJoinGame}
              disabled={actionLoading}
            >
              Join Game by ID
            </button>
          </div>
        </div>
      ) : (
        // --- Logged-out View (Login/Signup Form) ---
        <div className="login-form">
          <h3>Login Here</h3>
          <form onSubmit={handleSignIn}>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              id="email"
              required
            />
            <br />
            <label htmlFor="password">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              id="password"
              required
            />
            <br />
            <button
              type="submit"
              className="login-button"
              disabled={actionLoading}
            >
              {actionLoading ? "Logging In..." : "Log In"}
            </button>
            <button
              type="button"
              className="signup-button"
              onClick={handleSignUp}
              disabled={actionLoading}
            >
              {actionLoading ? "Signing Up..." : "Sign Up"}
            </button>
            <div className="join-game-container">
              <button
                type="button"
                className="join-game-button"
                onClick={goToJoinGame}
                disabled={actionLoading}
              >
                Join Game by ID
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default Login;
